import React from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import Comments from '../Comment/Comments';
import { LuArrowDownUp } from "react-icons/lu";
import moment from "moment";
import './Table.css'
import { BsSearch } from "react-icons/bs";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useNavigate } from 'react-router-dom';

const ColumnHelper = createColumnHelper();

function Table({ posts, setCurrentId, handleDeleteAlert }) {
    const navigate = useNavigate();
    const columns = [
        {
            id: 'sn',
            header: () => <span className="tableHeader " >S/N</span>,
            cell: ({ row }) => row.index + 1,
            meta: {
                className: 'sn-col',
                backgroundColor: '#fff',
            }
        },

        ColumnHelper.accessor('topic', {
            header: () => (
                <span className='tableHeader' >Title</span>
            ),
            cell: info => info.getValue(),
            meta: { style: { width: '350px' } }
        }),
        ColumnHelper.accessor('category', {
            header: () => (
                <span className='tableHeader'>Category</span>
            ),
            cell: info => info.getValue(),
        }),

        {
            id: 'comment',
            header: () => <span className="tableHeader">Comments</span>,
            cell: ({ row }) => <Comments postId={row.original.id} countOnly />,
        },
        ColumnHelper.accessor('postername', {
            header: () => (
                <span className='tableHeader'>Poster Name</span>
            ),
            cell: info => info.getValue(),
        }),
        {
            id: 'createdAt',
            header: () => <span className="tableHeader">Date</span>,
            cell: ({ row }) => {
                const date = row.original.createdAt;
                return (
                    <span>
                        {moment(date).format('MM')} {moment(date).format('Do')}, {moment(date).format('YYYY')}
                    </span>
                );
            },
        },

        {
            id: 'action',
            header: () => <span className="tableHeader">Action</span>,
            cell: ({ row }) => (
                <div className="actionButtons">
                    <button
                        className="edit"
                        onClick={() => {
                            setCurrentId(row.original.id);
                            navigate(`/editpost/${row.original.id}`);
                        }}
                    >
                        Edit
                    </button>

                    <button
                        className="delete"
                        onClick={() => handleDeleteAlert(row.original)}
                    >
                        Delete
                    </button>
                </div>
            ),
            meta: { style: { width: '220px' } }
        },
    ];
    const [postData] = React.useState(() => [...posts].reverse()); // Reverse the posts array to display the latest posts first
    const [sorting, setSorting] = React.useState([]);
    const [globalFilter, setGlobalFilter] = React.useState('');
    const [pagination, setPagination] = React.useState({
        pageIndex: 0,
        pageSize: 5,
    });

    const table = useReactTable({
        data: postData,
        enableSortingRemoval: true,
        columns,
        state: {
            sorting,
            globalFilter,
            pagination
        },
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),

        onGlobalFilterChange: setGlobalFilter,
        getFilteredRowModel: getFilteredRowModel(),

        onPaginationChange: setPagination,
        getPaginationRowModel: getPaginationRowModel()
    });

    console.log("tables", table.getRowModel())

    return (
        <div>
            <div className='inputContainer'>
                <input
                    type="text"
                    placeholder="Search..."
                    value={globalFilter || ''}
                    onChange={e => setGlobalFilter(e.target.value)}
                    className='searchInput'
                />

                <BsSearch className='search' />
            </div>
            <div style={{ overflowX: 'auto' }} className='tableContainer'>
                <table className="custom-table">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className={`${header.column.columnDef.meta?.className || ''}`}
                                        style={header.column.columnDef.meta?.style}
                                    >
                                        <div
                                            className={header.column.getCanSort() ? 'sorting' : ''}
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}

                                            {header.column.getCanSort() && <LuArrowDownUp />}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td
                                        key={cell.id}
                                        className={`${cell.column.columnDef.meta?.className || ''}`}
                                        style={cell.column.columnDef.meta?.style}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className='tablePagination'>
                <div className='paginationInfo'>
                    <span className='paginationText'>
                        Items per page
                    </span>

                    <select
                        className='pageSizeSelect'
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                    >
                        {[5, 10, 15, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="rightPagination">
                    <button
                        className="leftArrow"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <IoIosArrowBack size={20} />
                    </button>

                    <button
                        className="leftArrow"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <IoIosArrowBack size={20} />
                    </button>

                    <span className="incrementPage">
                        <input
                            min={1}
                            max={table.getPageCount()}
                            type="number"
                            value={table.getState().pagination.pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                table.setPageIndex(page);
                            }}
                            className="inputPageNumber"
                        />
                        <span className="ml-1">of {table.getPageCount()}</span>
                    </span>
                    <button
                        className="arrowRight"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        <IoIosArrowForward size={20} />
                    </button>

                    <button
                        className="arrowRight"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        <IoIosArrowForward size={20} />
                    </button>

                </div>
            </div>
        </div>
    )
}

export default Table